import type { TimelineState } from '../types.ts';

// ─── Timeline component ───────────────────────────────────────────────────────

export class Timeline {
  private slider: HTMLInputElement;
  private cursor: HTMLElement;
  private liveBadge: HTMLElement;
  private datePicker: HTMLInputElement;
  private onChangeCallback: ((state: TimelineState) => void) | null = null;
  private clockInterval: ReturnType<typeof setInterval> | null = null;
  private _state: TimelineState;

  constructor() {
    this.slider = document.getElementById('time-slider') as HTMLInputElement;
    this.cursor = document.getElementById('timeline-cursor') as HTMLElement;
    this.liveBadge = document.getElementById('timeline-live') as HTMLElement;
    this.datePicker = document.getElementById('date-picker') as HTMLInputElement;

    // Initialise to now
    const now = new Date();
    this._state = {
      date: now,
      minuteOfDay: now.getHours() * 60 + now.getMinutes(),
      isLive: true,
    };

    this.datePicker.valueAsDate = now;
    this.slider.value = String(this._state.minuteOfDay);
    this.updateCursor(this._state.minuteOfDay);

    this.attachListeners();
    this.startLiveClock();
  }

  get state(): TimelineState {
    return { ...this._state };
  }

  onChange(cb: (state: TimelineState) => void): this {
    this.onChangeCallback = cb;
    return this;
  }

  private attachListeners(): void {
    this.slider.addEventListener('input', () => {
      const minutes = parseInt(this.slider.value, 10);
      this.updateCursor(minutes);
      this._state = { ...this._state, minuteOfDay: minutes };
      this.checkLive();
      this.onChangeCallback?.(this.state);
    });

    this.datePicker.addEventListener('change', () => {
      const picked = this.datePicker.valueAsDate;
      if (!picked) return;
      this._state = { ...this._state, date: picked };
      this.checkLive();
      this.onChangeCallback?.(this.state);
    });
  }

  private updateCursor(minutes: number): void {
    const h = String(Math.floor(minutes / 60)).padStart(2, '0');
    const m = String(minutes % 60).padStart(2, '0');
    this.cursor.textContent = `${h}:${m}`;
  }

  private checkLive(): void {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const pickedStr = this._state.date.toISOString().slice(0, 10);
    const diffMins = Math.abs(this._state.minuteOfDay - (now.getHours() * 60 + now.getMinutes()));
    const isLive = pickedStr === todayStr && diffMins <= 5;
    this._state = { ...this._state, isLive };
    this.liveBadge.classList.toggle('live-badge--active', isLive);
  }

  private startLiveClock(): void {
    const update = (): void => {
      if (!this._state.isLive) return;
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      this._state = { ...this._state, date: now, minuteOfDay: minutes };
      this.slider.value = String(minutes);
      this.updateCursor(minutes);
    };
    update();
    this.clockInterval = setInterval(update, 30_000); // sync every 30 s
  }

  destroy(): void {
    if (this.clockInterval) clearInterval(this.clockInterval);
  }
}

// ─── World clock display ──────────────────────────────────────────────────────

export class WorldClock {
  private utcEl: HTMLElement;
  private localEl: HTMLElement;

  constructor() {
    this.utcEl = document.getElementById('clock-utc') as HTMLElement;
    this.localEl = document.getElementById('clock-local') as HTMLElement;
    this.tick();
    setInterval(() => this.tick(), 1000);
  }

  private tick(): void {
    const now = new Date();
    const utc = now.toUTCString().replace('GMT', 'UTC').split(' ').slice(4).join(' ');
    this.utcEl.textContent = utc;

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const local = now.toLocaleTimeString(undefined, { timeZone: tz, hour: '2-digit', minute: '2-digit' });
    this.localEl.textContent = `${local} (${tz.replace(/_/g, ' ')})`;
  }

  /** Update the local clock display to reflect the time zone of a lat/lng pair */
  updateForLocation(_lat: number, _lng: number): void {
    // Time-zone-from-coordinates requires an external library or API.
    // For now the local clock always shows the user's own time zone.
  }
}
